'use client'
import type { RichTextField } from '@prismicio/client'
import React from 'react'
import Image from 'next/image'
import CareersBreadcrumb from './CareersBreadcrumb'

import { cn } from '@/utils/twMerge'
import { PrismicNextImage } from '@prismicio/next'
import { smoothScrollToElement } from '@/utils/smoothScroll'
import { PrismicLink, PrismicRichText } from '@prismicio/react'
import { getDescription, getHeading, getHeroBtnLabel, getHeroBtnLink, getHeroImage } from '@/utils/getHeroData'

type DataObject = Record<string, any>

type HeroTwoProps = {
  data: DataObject
  type?: string
  uid?: string
  showTag?: boolean
  showButton?: boolean
  media?: React.ReactNode
}

const HeroTwo = ({ data, type, uid, showTag = false, showButton = true, media }: HeroTwoProps) => {
  const notShowBg: string[] = ['companySmallHero', 'customers', 'contact', 'careers']

  const getBgImage = (type: string) => {
    const obj = {
      careers: '/images/careers-bg.svg',
    }
    return obj[type as keyof typeof obj] || '/images/hero-two-bg.jpg'
  }

  const getTypeStyles = (type?: string) => {
    const styles: Record<string, string> = {
      companySmallHero: 'bg-secondary-gray-10',
      customers: 'bg-secondary-gray-10',
      contact: 'bg-secondary-gray-10',
      careers: 'pt-8',
    }
    return type ? styles[type] || '' : ''
  }

  const getUidStyles = (uid?: string) => {
    const styles: Record<string, string> = {
      cmmc: 'lg:!pt-2 !pb-0',
      'compliance-platform': 'bg-primary-ink-100',
    }
    return uid ? styles[uid] || '' : ''
  }

  const isCompanySmallHero = type === 'companySmallHero'

  const renderButton = () => {
    if (!showButton) return null

    if (type === 'careers') {
      return (
        <a
          href="#jobs"
          onClick={(e) => {
            e.preventDefault()
            smoothScrollToElement('jobs', 0)
            // Update URL hash without causing page jump
            window.history.pushState(null, '', '#jobs')
          }}
          className="btn btn-primary btn-default btn-interaction !w-fit"
          aria-label={`${data.page_header_button_text} - Navigate to jobs section`}>
          {data.page_header_button_text}
          <Image src="/images/angle-right-green.svg" width={20} height={16} alt="" aria-hidden="true" />
        </a>
      )
    }

    const btnLabel = getHeroBtnLabel(type as string, uid as string, data)

    return (
      <PrismicLink
        field={getHeroBtnLink(type as string, uid as string, data)}
        className={cn(
          'btn btn-interaction !w-fit',
          uid === 'cmmc' ? 'btn-secondary btn-small' : 'btn-primary btn-default',
          uid === 'compliance-platform' ? 'btn-white btn-small' : '',
        )}>
        {type === 'framework' ? (
          <PrismicRichText field={data.demo_button_text} />
        ) : typeof btnLabel === 'string' ? (
          btnLabel
        ) : (
          <PrismicRichText field={btnLabel as RichTextField} />
        )}
        <Image
          src={uid === 'cmmc' ? '/images/angle-right-black.svg' : '/images/angle-right-green.svg'}
          width={20}
          height={16}
          alt=""
          aria-hidden="true"
        />
      </PrismicLink>
    )
  }

  const renderCopy = () => (
    <>
      {showTag && (
        <span className="mb-6 block pl-1 text-sm font-semibold uppercase text-primary-green-150">
          {data.tag}
        </span>
      )}

      <div
        className={`[&>h1]:text-[calc(2.325rem+.9vw)] [&>h1]:leading-[100%] [&>h1]:tracking-[-1.28px] [&>h1]:xl:text-[4rem] [&>h2]:text-[calc(2.325rem+.9vw)] [&>h2]:leading-[100%] [&>h2]:tracking-[-1.28px] ${type === 'frameworks-glossary-details' ? '[&>h2]:xl:text-[3rem]' : '[&>h2]:xl:text-[4rem]'} `}>
        <PrismicRichText
          field={getHeading(type as string, uid as string, data)}
          components={{
            heading2: ({ children }) => (
              <h1 className={cn(uid === 'compliance-platform' ? 'text-white' : '')}>{children}</h1>
            ),
          }}
        />
      </div>

      <div className="[&>p]:my-8">
        <PrismicRichText
          field={getDescription(type as string, uid as string, data)}
          components={{
            paragraph: ({ children }) => (
              <p
                className={cn(
                  uid === 'cmmc' || uid === 'compliance-platform'
                    ? 'text-base font-normal leading-[140%] tracking-[1%] lg:text-[22px]'
                    : '',
                  uid === 'compliance-platform' ? 'text-white' : '',
                )}>
                {children}
              </p>
            ),
          }}
        />
      </div>

      {renderButton()}
    </>
  )

  return (
    <React.Fragment>
      {/* CMMC breadcrumb lives in (marketing)/[uid]/layout so tab nav keeps chrome mounted */}
      {type === 'careers' && <CareersBreadcrumb />}

      <section
        className={cn(
          'relative z-[1]',
          !isCompanySmallHero && 'pb-[78px] pt-16',
          uid,
          getTypeStyles(type),
          getUidStyles(uid),
        )}>
        {!notShowBg.includes(type as string) && !['cmmc', 'compliance-platform'].includes(uid as string) && (
          <div className="absolute right-[0] top-[0] -z-[1] h-auto w-full">
            <Image
              src={getBgImage(type as string)}
              alt=""
              aria-hidden="true"
              width={1440}
              height={type === 'careers' ? 1303 : 1764}
              sizes="100vw"
              priority
              className="h-auto w-full object-contain object-top"
            />
          </div>
        )}

        {isCompanySmallHero ? (
          <div className="lg:grid lg:grid-cols-2">
            <div className="flex lg:justify-end">
              <div className="flex w-full flex-col justify-center px-4 py-16 sm:px-6 md:px-8 md:py-20 lg:max-w-[640px] lg:py-24 lg:pl-8 lg:pr-[60px]">
                {renderCopy()}
              </div>
            </div>
            <div className="relative h-[320px] w-full overflow-hidden sm:h-[420px] lg:h-auto lg:min-h-[640px]">
              <PrismicNextImage
                field={getHeroImage(type as string, uid as string, data)}
                fill
                sizes="(max-width: 1024px) 100vw, 50vw"
                className={cn('object-cover', uid === 'company-enterprise' && 'object-left-bottom')}
                fallbackAlt=""
              />
            </div>
          </div>
        ) : (
          <div className="container">
            <div className="flex flex-col items-center lg:flex-row">
              <div
                className={cn(
                  'w-full lg:w-2/4 lg:pr-[60px]',
                  ['trust', 'careers'].includes(type || '') ? 'flex flex-col justify-center' : '',
                )}>
                {renderCopy()}
              </div>

              <div className="mt-8 w-full lg:mt-0 lg:w-2/4">
                {media ?? (
                  <PrismicNextImage
                    field={getHeroImage(type as string, uid as string, data)}
                    className="h-auto w-full"
                    fallbackAlt=""
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </section>
    </React.Fragment>
  )
}

export default HeroTwo
